// useSpeechMarkPlayerWeb.ts
import React, { useCallback, useRef, useState } from "react"

import { NodeType, TSentence, TViseme, TWord } from "types/speechMarks"

import type { RiveInputName } from "../utils/pollyVisemeTable"
import type { Rive } from "@rive-app/canvas"
import { getRiveInput, INPUT_NAMES } from "../utils/pollyVisemeTable"

interface CurrentViseme {
  value: string
  time: number
  interpTime: number
  riveInput: string
}

interface RiveWebRef {
  setInputState: (stateMachine: string, input: string, value: number) => boolean
  animateViseme: (duration: number, targetInput: RiveInputName, easing: string) => void
  stopVisemeAnimation: () => void
  isReady: () => boolean
}

interface CaptionSegment {
  startTime: number
  endTime: number
  line1: string
  line2: string
  wordIndices: number[]
}

interface CaptionText {
  line1: string
  line2: string
}

const easingFunctions = {
  EaseInOutQuad: (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  EaseInQuad: (t: number) => t * t,
  EaseOutQuad: (t: number) => t * (2 - t),
  Linear: (t: number) => t,
}

// WEB CC SETTINGS
const MAX_CHARS_PER_LINE = 32
const MIN_DISPLAY_TIME_MS = 1000
const CAPTION_LEAD_MS = 0
const CAPTION_LAG_MS = 800

export function useSpeechMarkPlayerWeb(
  riveInstanceRef: React.RefObject<Rive | null>,
  stateMachineName: string = "State Machine 1",
) {
  const [isAnimating, setIsAnimating] = useState(false)
  const [currentViseme, setCurrentViseme] = useState<CurrentViseme | null>(null)
  const [captionText, setCaptionText] = useState<CaptionText>({ line1: "", line2: "" })
  const [isCaptionActive, setIsCaptionActive] = useState(false)

  const scheduledTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([])
  const activeAnimationRef = useRef<number | null>(null)
  const currentTargetViseme = useRef<RiveInputName>("CLOSE")
  const captionAnimationFrameRef = useRef<number | null>(null)
  const captionStartTimeRef = useRef<number>(0)
  const captionSegmentsRef = useRef<CaptionSegment[]>([])
  const currentSegmentIndexRef = useRef<number>(0)

  const riveWebRef = useRef<RiveWebRef | null>(null)

  // Initialize the ref when Rive instance is available
  React.useEffect(() => {
    const riveInstance = riveInstanceRef.current

    if (riveInstance) {
      riveWebRef.current = {
        isReady: () => {
          try {
            if (!riveInstance) {
              return false
            }

            if (typeof riveInstance.stateMachineInputs !== "function") {
              return false
            }

            const inputs = riveInstance.stateMachineInputs(stateMachineName)

            const result = inputs && inputs.length > 0
            return result
          } catch (error) {
            console.error("[RIVE_WEB_REF] Exception in isReady():", error)
            return false
          }
        },

        setInputState: (stateMachine: string, inputName: string, value: number) => {
          try {
            const inputs = riveInstance.stateMachineInputs(stateMachine)
            if (inputs) {
              const input = inputs.find((i) => i.name === inputName)
              if (input) {
                ;(input as any).value = value
                return true
              }
            }
            return false
          } catch (error) {
            console.warn("[RIVE_WEB_REF] Error setting input state:", error)
            return false
          }
        },

        animateViseme: (duration: number, targetInput: RiveInputName, easing: string) => {
          try {
            const inputs = riveInstance.stateMachineInputs(stateMachineName)
            if (!inputs) return

            if (activeAnimationRef.current) {
              cancelAnimationFrame(activeAnimationRef.current)
            }

            const previousTarget = currentTargetViseme.current
            currentTargetViseme.current = targetInput

            const previousValues: Record<string, number> = {}
            INPUT_NAMES.forEach((inputName) => {
              const input = inputs.find((i) => i.name === inputName)
              previousValues[inputName] = input
                ? (input.value as number)
                : inputName === previousTarget
                  ? 100
                  : 0
            })

            const startTime = performance.now()
            const easingFunc =
              easingFunctions[easing as keyof typeof easingFunctions] ||
              easingFunctions.EaseInOutQuad

            const animate = (currentTime: number) => {
              try {
                const liveInputs = riveInstance.stateMachineInputs(stateMachineName)
                if (!liveInputs) {
                  activeAnimationRef.current = null
                  return
                }

                const elapsed = currentTime - startTime
                const progress = Math.min(elapsed / duration, 1)

                if (progress >= 1) {
                  INPUT_NAMES.forEach((inputName) => {
                    const input = liveInputs.find((i) => i.name === inputName)
                    if (input) {
                      ;(input as any).value = inputName === targetInput ? 100 : 0
                    }
                  })
                  activeAnimationRef.current = null
                  return
                }

                const easedProgress = easingFunc(progress)

                INPUT_NAMES.forEach((inputName) => {
                  const input = liveInputs.find((i) => i.name === inputName)
                  if (input) {
                    const previousValue = previousValues[inputName]
                    const targetValue = inputName === targetInput ? 100 : 0
                    const currentValue = Math.round(
                      previousValue + (targetValue - previousValue) * easedProgress,
                    )
                    ;(input as any).value = currentValue
                  }
                })

                activeAnimationRef.current = requestAnimationFrame(animate)
              } catch (error) {
                console.warn("[RIVE_WEB_REF] Animation error:", error)
                activeAnimationRef.current = null
              }
            }

            activeAnimationRef.current = requestAnimationFrame(animate)
          } catch (error) {
            console.warn("[RIVE_WEB_REF] Error starting animation:", error)
          }
        },

        stopVisemeAnimation: () => {
          if (activeAnimationRef.current) {
            cancelAnimationFrame(activeAnimationRef.current)
            activeAnimationRef.current = null
          }

          try {
            const inputs = riveInstance.stateMachineInputs(stateMachineName)
            if (inputs) {
              INPUT_NAMES.forEach((inputName) => {
                const input = inputs.find((i) => i.name === inputName)
                if (input) {
                  ;(input as any).value = inputName === "CLOSE" ? 100 : 0
                }
              })
            }
          } catch (error) {
            console.warn("[RIVE_WEB_REF] Error stopping animation:", error)
          }
        },
      }
    } else {
      console.log("[SPEECH_MARKS_WEB] No rive instance, clearing interface")
      riveWebRef.current = null
    }
  }, [riveInstanceRef.current, stateMachineName])

  // Set mouth to "CLOSE"
  React.useEffect(() => {
    const webRef = riveWebRef.current
    if (webRef && webRef.isReady()) {
      console.log("Web Rive instance available - initializing to CLOSE state")
      try {
        INPUT_NAMES.forEach((inputName) => {
          webRef.setInputState("State Machine 1", inputName, inputName === "CLOSE" ? 100 : 0)
        })
        currentTargetViseme.current = "CLOSE"
        console.log("Web Rive initialized to CLOSE state successfully")
      } catch (error) {
        console.warn("Failed to initialize Web Rive to CLOSE state:", error)
      }
    }
  }, [riveWebRef.current])

  // ===== CAPTION UTILITY FUNCTIONS =====

  const tokenizeWithPunctuation = useCallback((text: string): string[] => {
    return text.split(/\s+/).filter((w) => w.length > 0)
  }, [])

  /**
   * Split tokens into exactly two lines with pyramid style (line1 >= line2)
   */
  const splitIntoTwoLines = useCallback(
    (tokens: string[]): { line1: string; line2: string; line1WordCount: number } | null => {
      const fullText = tokens.join(" ")

      if (fullText.length <= MAX_CHARS_PER_LINE) {
        return { line1: fullText, line2: "", line1WordCount: tokens.length }
      }

      // Try to split into two lines with pyramid style (line1 >= line2)
      let bestSplit = -1
      let bestDifference = Infinity

      for (let i = Math.floor(tokens.length / 2); i < tokens.length; i++) {
        const line1Tokens = tokens.slice(0, i + 1)
        const line2Tokens = tokens.slice(i + 1)

        const line1Text = line1Tokens.join(" ")
        const line2Text = line2Tokens.join(" ")

        if (line1Text.length > MAX_CHARS_PER_LINE || line2Text.length > MAX_CHARS_PER_LINE) {
          continue
        }

        if (line1Text.length < line2Text.length) {
          continue
        }

        // Find most balanced split
        const difference = line1Text.length - line2Text.length
        if (difference < bestDifference) {
          bestDifference = difference
          bestSplit = i
        }
      }

      if (bestSplit === -1) {
        return null
      }

      // Use best split found
      const line1Tokens = tokens.slice(0, bestSplit + 1)
      const line2Tokens = tokens.slice(bestSplit + 1)

      const line1Text = line1Tokens.join(" ")
      const line2Text = line2Tokens.join(" ")

      return {
        line1: line1Text,
        line2: line2Text,
        line1WordCount: line1Tokens.length,
      }
    },
    [],
  )

  /**
   * Create caption segments from sentence and word marks
   * Ensures no text wrapping by creating multiple segments if needed
   */
  const prepareCaptions = useCallback(
    (speechMarks: Array<TSentence | TWord | TViseme>): CaptionSegment[] => {
      const wordMarks = speechMarks.filter((m): m is TWord => m.type === NodeType.WORD)
      const sentenceMarks = speechMarks.filter((m): m is TSentence => m.type === NodeType.SENTENCE)

      if (sentenceMarks.length === 0 || wordMarks.length === 0) {
        console.log("[CAPTIONS] No sentence or word marks found")
        return []
      }

      console.log("[CAPTIONS] Processing sentences:", sentenceMarks.length)

      const segments: CaptionSegment[] = []

      sentenceMarks.forEach((sentence, sentenceIdx) => {
        const wordsInSentence = wordMarks.filter(
          (w) => w.start >= sentence.start && w.end <= sentence.end,
        )

        if (wordsInSentence.length === 0) {
          console.warn("[CAPTIONS] No words found for sentence:", sentence.value)
          return
        }

        const tokens = tokenizeWithPunctuation(sentence.value)

        // console.log('[CAPTIONS] Sentence tokens:', tokens);
        // console.log('[CAPTIONS] Word marks:', wordsInSentence.length);

        let tokenIndex = 0
        let wordIndex = 0

        while (tokenIndex < tokens.length && wordIndex < wordsInSentence.length) {
          let segmentTokens: string[] = []
          let segmentWordIndices: number[] = []
          let result = null

          let testTokenIndex = tokenIndex

          while (testTokenIndex < tokens.length) {
            const testTokens = tokens.slice(tokenIndex, testTokenIndex + 1)
            const testResult = splitIntoTwoLines(testTokens)

            if (testResult === null) {
              break
            }

            result = testResult
            segmentTokens = testTokens
            segmentWordIndices = Array.from({ length: testTokens.length }, (_, i) => wordIndex + i)
            testTokenIndex++
          }

          if (result === null || segmentTokens.length === 0) {
            const singleToken = tokens[tokenIndex]
            // console.warn(`[CAPTIONS] Single token too long (${singleToken.length} chars): "${singleToken}"`);

            result = {
              line1: singleToken,
              line2: "",
              line1WordCount: 1,
            }
            segmentTokens = [singleToken]
            segmentWordIndices = [wordIndex]
            tokenIndex++
            wordIndex++
          } else {
            tokenIndex += segmentTokens.length
            wordIndex += segmentTokens.length
          }

          const { line1, line2 } = result

          // Determine timing
          const firstWord = wordsInSentence[segmentWordIndices[0]]
          const lastWord = wordsInSentence[segmentWordIndices[segmentWordIndices.length - 1]]

          // Safety check: if words are missing, fall back to sentence time or previous segment end
          if (!firstWord || !lastWord) {
            console.warn("[CAPTIONS] Missing word marks for segment:", {
              line1,
              line2,
              wordIndex,
              wordsAvailable: wordsInSentence.length,
            })

            const startTime =
              segments.length > 0 ? segments[segments.length - 1].endTime : sentence.time
            const endTime = startTime + MIN_DISPLAY_TIME_MS

            segments.push({
              startTime,
              endTime,
              line1,
              line2,
              wordIndices: segmentWordIndices,
            })
            continue
          }

          const startTime = Math.max(0, firstWord.time - CAPTION_LEAD_MS)

          let endTime: number
          const nextWordIndex = segmentWordIndices[segmentWordIndices.length - 1] + 1

          if (nextWordIndex < wordsInSentence.length) {
            endTime = wordsInSentence[nextWordIndex].time
          } else {
            const nextSentence = sentenceMarks[sentenceIdx + 1]
            if (nextSentence) {
              endTime = nextSentence.time
            } else {
              endTime = lastWord.time + CAPTION_LAG_MS
            }
          }

          endTime = Math.max(endTime, startTime + MIN_DISPLAY_TIME_MS)

          segments.push({
            startTime,
            endTime,
            line1,
            line2,
            wordIndices: segmentWordIndices,
          })

          // console.log(`[CAPTIONS] Segment ${segments.length}:`, {
          //   line1: `"${line1}" (${line1.length} chars)`,
          //   line2: `"${line2}" (${line2.length} chars)`,
          //   pyramid: line1.length >= line2.length ? '✓' : '✗',
          //   startTime: `${startTime}ms`,
          //   endTime: `${endTime}ms`,
          //   duration: `${endTime - startTime}ms`,
          //   wordCount: segmentWordIndices.length
          // });
        }
      })

      return segments
    },
    [tokenizeWithPunctuation, splitIntoTwoLines],
  )

  const captionLoop = useCallback(
    (timestamp: number) => {
      const elapsed = timestamp - captionStartTimeRef.current
      const segments = captionSegmentsRef.current
      const currentIndex = currentSegmentIndexRef.current

      if (currentIndex >= segments.length) {
        setCaptionText({ line1: "", line2: "" })
        setIsCaptionActive(false)
        return
      }

      const currentSegment = segments[currentIndex]

      // Display current segment
      if (elapsed >= currentSegment.startTime && elapsed < currentSegment.endTime) {
        setCaptionText({
          line1: currentSegment.line1,
          line2: currentSegment.line2,
        })
      }

      // Move to next segment
      if (elapsed >= currentSegment.endTime) {
        currentSegmentIndexRef.current++

        if (currentSegmentIndexRef.current < segments.length) {
          const nextSegment = segments[currentSegmentIndexRef.current]
          const gap = nextSegment.startTime - elapsed

          if (gap > 200) {
            setCaptionText({ line1: "", line2: "" })
          }
        } else {
          setCaptionText({ line1: "", line2: "" })
        }
      }

      if (isCaptionActive) {
        captionAnimationFrameRef.current = requestAnimationFrame(captionLoop)
      }
    },
    [isCaptionActive],
  )

  const startCaptionPlayback = useCallback(
    (speechMarks: Array<TSentence | TWord | TViseme>) => {
      const segments = prepareCaptions(speechMarks)

      if (segments.length === 0) {
        console.log("[CAPTIONS] No caption segments generated")
        return false
      }

      // console.log(`[CAPTIONS] Starting playback with ${segments.length} segments`);

      captionSegmentsRef.current = segments
      currentSegmentIndexRef.current = 0
      setIsCaptionActive(true)
      captionStartTimeRef.current = performance.now()
      captionAnimationFrameRef.current = requestAnimationFrame(captionLoop)

      return true
    },
    [prepareCaptions, captionLoop],
  )

  const stopCaptions = useCallback(() => {
    if (captionAnimationFrameRef.current) {
      cancelAnimationFrame(captionAnimationFrameRef.current)
      captionAnimationFrameRef.current = null
    }
    setIsCaptionActive(false)
    setCaptionText({ line1: "", line2: "" })
    currentSegmentIndexRef.current = 0
    captionSegmentsRef.current = []
  }, [])

  // Keep caption loop running when active
  React.useEffect(() => {
    if (isCaptionActive && !captionAnimationFrameRef.current) {
      captionAnimationFrameRef.current = requestAnimationFrame(captionLoop)
    }
    return () => {
      if (captionAnimationFrameRef.current) {
        cancelAnimationFrame(captionAnimationFrameRef.current)
        captionAnimationFrameRef.current = null
      }
    }
  }, [isCaptionActive, captionLoop])

  // ===== MAIN CONTROL FUNCTIONS =====

  const stop = useCallback(() => {
    console.log("Stopping all animations")
    scheduledTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout))
    scheduledTimeoutsRef.current = []
    riveWebRef.current?.stopVisemeAnimation()
    setIsAnimating(false)
    setCurrentViseme(null)
    stopCaptions()
  }, [stopCaptions])

  const animateSpeech = useCallback(
    async (
      speechMarkData: string | Array<TSentence | TWord | TViseme>,
      offsetMs = 0,
    ): Promise<boolean> => {
      const rive = riveWebRef.current
      if (!rive || !rive.isReady()) {
        console.error("Rive instance not available or not ready")
        return false
      }

      stop()

      try {
        // Parse speech marks
        let allMarks: Array<TSentence | TWord | TViseme>
        if (typeof speechMarkData === "string") {
          allMarks = speechMarkData.includes("\n")
            ? speechMarkData
                .split("\n")
                .filter((line) => line.trim())
                .map((line) => JSON.parse(line))
            : JSON.parse(speechMarkData)
        } else {
          allMarks = speechMarkData
        }

        // Start captions
        startCaptionPlayback(allMarks)

        // Filter and sort viseme marks
        const visemeMarks = allMarks
          .filter((mark): mark is TViseme => mark.type === NodeType.VISEME)
          .sort((a, b) => a.time - b.time)

        if (visemeMarks.length === 0) {
          console.warn("No viseme marks found")
          return false
        }

        // When resuming partway through the clip, skip visemes that should have
        // already played and start from the one aligned with the current audio
        // position. With offsetMs === 0 this is index 0 (normal full playback).
        const startIndex = (() => {
          const idx = visemeMarks.findIndex((mark) => mark.time >= offsetMs)
          return idx === -1 ? visemeMarks.length : idx
        })()

        console.log(
          `Scheduling ${visemeMarks.length - startIndex} visemes (offset ${offsetMs}ms, skipped ${startIndex})`,
        )

        // Start with mouth closed
        rive.setInputState("State Machine 1", "CLOSE", 100)
        setIsAnimating(true)

        // Snap to the viseme that should already be showing at the current offset
        // so the mouth doesn't restart from a closed position mid-word.
        if (startIndex > 0) {
          const elapsedMark = visemeMarks[startIndex - 1]
          const nextMark = visemeMarks[startIndex]
          const interpTime = nextMark ? nextMark.time - elapsedMark.time : 150
          const riveInput = getRiveInput(elapsedMark.value)
          setCurrentViseme({
            value: elapsedMark.value,
            time: elapsedMark.time,
            interpTime,
            riveInput,
          })
          rive.animateViseme(interpTime, riveInput, "EaseInOutQuad")
        }

        // The whole clip already elapsed — nothing left to schedule.
        if (startIndex >= visemeMarks.length) {
          setIsAnimating(false)
          return true
        }

        // Schedule the remaining (future) visemes, offset so each fires when the
        // audio actually reaches it.
        for (let index = startIndex; index < visemeMarks.length; index++) {
          const mark = visemeMarks[index]
          const nextMark = visemeMarks[index + 1]
          const interpTime = nextMark ? nextMark.time - mark.time : 150
          const riveInput = getRiveInput(mark.value)

          const timeout = setTimeout(() => {
            const currentRive = riveWebRef.current
            if (!currentRive) return

            setCurrentViseme({
              value: mark.value,
              time: mark.time,
              interpTime,
              riveInput,
            })

            currentRive.animateViseme(interpTime, riveInput, "EaseInOutQuad")

            // Clean up after last viseme
            if (index === visemeMarks.length - 1) {
              setTimeout(() => {
                setIsAnimating(false)
                setCurrentViseme(null)
              }, interpTime)
            }
          }, mark.time - offsetMs)

          scheduledTimeoutsRef.current.push(timeout)
        }

        return true
      } catch (error) {
        console.error("Error playing speech:", error)
        stop()
        return false
      }
    },
    [stop, startCaptionPlayback],
  )

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (captionAnimationFrameRef.current) {
        cancelAnimationFrame(captionAnimationFrameRef.current)
      }
    }
  }, [])

  return {
    animateSpeech,
    stop,
    isAnimating,
    currentViseme,
    isRiveReady: riveWebRef.current?.isReady() ?? false,
    captionText,
    isCaptionActive,
  }
}

export default useSpeechMarkPlayerWeb
